// Scene-wide "spotlight" dim for the search feature. Walks the scene's
// meshes and multiplies each material's color (and emissive, if any)
// down to DIM_FACTOR — except for materials in the exempt set, which
// stay at original brightness.
//
// Stashes original color values on `material.userData` the first time
// it dims a material, so clearSpotlight can restore them verbatim. This
// matters because materials can be shared (e.g. the cup HANDLE_MATERIAL
// is one instance reused across all cups) and a stash-then-restore
// cycle keeps that shared state correct.
//
// Materials touched: anything reachable via scene.traverse() that has a
// .color (MeshStandardMaterial, MeshBasicMaterial, etc.). Cat materials
// load asynchronously; if the cat hasn't loaded yet, its meshes won't
// be in the scene and won't be dimmed — re-applying the spotlight after
// the cat loads (or accepting a brief inconsistency) is the caller's
// choice.

import * as THREE from "three";

const DIM_FACTOR = 0.18;

interface DimmableMaterial extends THREE.Material {
  color?: THREE.Color;
  emissive?: THREE.Color;
  emissiveIntensity?: number;
}

interface OrigSnapshot {
  color?: THREE.Color;
  emissive?: THREE.Color;
  emissiveIntensity?: number;
}

const STASH_KEY = "__searchSpotlightOrig";

function snapshot(mat: DimmableMaterial): OrigSnapshot | null {
  if (!mat.color) return null;
  const snap: OrigSnapshot = { color: mat.color.clone() };
  if (mat.emissive) {
    snap.emissive = mat.emissive.clone();
    snap.emissiveIntensity = mat.emissiveIntensity;
  }
  return snap;
}

function dimMaterial(mat: DimmableMaterial): void {
  if (!mat.color) return;
  if (!mat.userData[STASH_KEY]) {
    const snap = snapshot(mat);
    if (!snap) return;
    mat.userData[STASH_KEY] = snap;
  }
  const orig = mat.userData[STASH_KEY] as OrigSnapshot;
  mat.color.copy(orig.color!).multiplyScalar(DIM_FACTOR);
  if (mat.emissive && orig.emissive) {
    mat.emissive.copy(orig.emissive).multiplyScalar(DIM_FACTOR);
  }
}

function restoreMaterial(mat: DimmableMaterial): void {
  const stash = mat.userData[STASH_KEY] as OrigSnapshot | undefined;
  if (!stash) return;
  if (mat.color && stash.color) mat.color.copy(stash.color);
  if (mat.emissive && stash.emissive) mat.emissive.copy(stash.emissive);
  if (stash.emissiveIntensity !== undefined) {
    mat.emissiveIntensity = stash.emissiveIntensity;
  }
  delete mat.userData[STASH_KEY];
}

function forEachMaterial(
  mesh: THREE.Mesh | THREE.SkinnedMesh,
  fn: (m: DimmableMaterial) => void,
): void {
  if (Array.isArray(mesh.material)) {
    for (const m of mesh.material) fn(m as DimmableMaterial);
  } else {
    fn(mesh.material as DimmableMaterial);
  }
}

/**
 * Dim every mesh material in the scene, except those in `exempt`.
 * Idempotent — repeat calls re-dim from the stashed original, so a
 * material added to (or removed from) the exempt set across calls
 * doesn't compound.
 */
export function applySpotlight(
  scene: THREE.Scene,
  exempt: Set<THREE.Material>,
): void {
  scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    forEachMaterial(mesh, (mat) => {
      if (exempt.has(mat)) {
        // Newly-exempt this call — undo any previous dim.
        restoreMaterial(mat);
      } else {
        dimMaterial(mat);
      }
    });
  });
}

/** Restore every material the spotlight has touched. Safe to call when
 * no spotlight is active (no-op). */
export function clearSpotlight(scene: THREE.Scene): void {
  scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    forEachMaterial(mesh, restoreMaterial);
  });
}
